import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  List,
  Tag,
  Typography,
  Space,
  Spin,
  Empty,
  Alert,
  App,
} from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import cioService from '../../Services/cioService';
import { formatDateTime } from '../../Utils/format';
import { toApiError } from '../../api/axiosClient';

/**
 * Controlled customer support-access audit (Remaining Handoff, Flow E).
 * A record is "open" while completedAtUtc is null and "complete" once an outcome
 * is recorded - there is no numeric status enum, so state is derived from
 * completedAtUtc only. Starting access requires a Ready customer deployment and
 * completed secure environment configuration; the API returns 409 with a
 * business message when a prerequisite is missing.
 */
const SupportAccessPanel = ({ clientId }) => {
  const { message } = App.useApp();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [startOpen, setStartOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startForm] = Form.useForm();

  const [completeRecord, setCompleteRecord] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [completeForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await cioService.getSupportAccess(clientId);
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setLoadError(toApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const submitStart = async () => {
    let values;
    try {
      values = await startForm.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    setStarting(true);
    try {
      await cioService.startSupportAccess(clientId, values);
      message.success('Support access started.');
      setStartOpen(false);
      startForm.resetFields();
      load();
    } catch (err) {
      // 409 => a prerequisite is incomplete; surface the returned business message.
      message.error(toApiError(err).message);
    } finally {
      setStarting(false);
    }
  };

  const submitComplete = async () => {
    let values;
    try {
      values = await completeForm.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    setCompleting(true);
    try {
      await cioService.completeSupportAccess(clientId, completeRecord.id, values);
      message.success('Support access completed.');
      setCompleteRecord(null);
      completeForm.resetFields();
      load();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setCompleting(false);
    }
  };

  // Open records (completedAtUtc null) first, then completed; newest first within each group.
  const sorted = [...records].sort((a, b) => {
    const aOpen = !a.completedAtUtc;
    const bOpen = !b.completedAtUtc;
    if (aOpen !== bOpen) return aOpen ? -1 : 1;
    return new Date(b.createdOn) - new Date(a.createdOn);
  });

  return (
    <Card
      title="Support Access"
      size="small"
      extra={
        <Button icon={<SafetyCertificateOutlined />} onClick={() => setStartOpen(true)}>
          Start Support Access
        </Button>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      ) : loadError ? (
        <Typography.Text type="danger">{loadError.message}</Typography.Text>
      ) : sorted.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No support-access records yet." />
      ) : (
        <List
          itemLayout="vertical"
          dataSource={sorted}
          renderItem={(r) => {
            const open = !r.completedAtUtc;
            return (
              <List.Item
                key={r.id}
                actions={
                  open
                    ? [
                        <Button
                          key="complete"
                          size="small"
                          type="primary"
                          onClick={() => setCompleteRecord(r)}
                        >
                          Complete
                        </Button>,
                      ]
                    : undefined
                }
              >
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Space>
                    <strong>{r.accessType}</strong>
                    {open ? <Tag color="gold">Open</Tag> : <Tag color="green">Completed</Tag>}
                  </Space>
                  <Typography.Text type="secondary">{r.purpose}</Typography.Text>
                  <Typography.Text style={{ fontSize: 12, color: '#667085' }}>
                    Started {formatDateTime(r.createdOn)}
                    {!open && ` · Completed ${formatDateTime(r.completedAtUtc)}`}
                  </Typography.Text>
                  {!open && r.outcome ? (
                    <Typography.Text style={{ fontSize: 13 }}>Outcome: {r.outcome}</Typography.Text>
                  ) : null}
                </Space>
              </List.Item>
            );
          }}
        />
      )}

      <Modal
        title="Start Support Access"
        open={startOpen}
        onOk={submitStart}
        onCancel={() => setStartOpen(false)}
        confirmLoading={starting}
        okText="Start access"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="This records a controlled support-access session for audit. It requires a Ready customer deployment and completed secure environment configuration."
        />
        <Form form={startForm} layout="vertical" requiredMark="optional">
          <Form.Item name="accessType" label="Access type" rules={[{ required: true }, { max: 100 }]}>
            <Input placeholder="e.g. Application investigation" maxLength={100} />
          </Form.Item>
          <Form.Item name="purpose" label="Purpose" rules={[{ required: true }, { max: 1000 }]}>
            <Input.TextArea
              rows={3}
              maxLength={1000}
              placeholder="Business reason for this access (e.g. investigate the issue in TS-A1B2C3)."
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Complete Support Access"
        open={!!completeRecord}
        onOk={submitComplete}
        onCancel={() => setCompleteRecord(null)}
        confirmLoading={completing}
        okText="Complete access"
        destroyOnHidden
      >
        <Form form={completeForm} layout="vertical" requiredMark="optional">
          <Form.Item
            name="outcome"
            label="Outcome"
            rules={[
              { required: true, message: 'Please add a short outcome note before completing.' },
              { max: 1000 },
            ]}
          >
            <Input.TextArea
              rows={3}
              maxLength={1000}
              placeholder="What was done and verified during this access."
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default SupportAccessPanel;
